import { Searcher } from "fast-fuzzy";
import _ from "lodash";
import { getItem, setItem } from "utils/localstorage";
import {
    getEmptyConsoleObjects,
    mapUnifiedSearchResponse
} from "./consoleObjects";

const isStaleConsoleObjectsResponse = (consoleObjects, request, requestId) =>
    consoleObjects?.requestId !== requestId ||
    consoleObjects?.requestedSearchTerm !== request?.payload?.searchTerm ||
    consoleObjects?.requestedLimit !== request?.payload?.limit;

const referenceRequestKey = type => type.includes("DOCUMENTATION") ? "documentation" : "knowledgeBase";

const isStaleReferenceResponse = (documentationResults, sectionKey, request, requestId) => {
    const tracked = documentationResults?.requests?.[sectionKey];
    if (!tracked?.searchTerm) {
        return false;
    }
    if (tracked.searchTerm !== request?.payload?.searchTerm) {
        return true;
    }
    return Boolean(tracked.requestId) && tracked.requestId !== requestId;
};

const defaultState = () => ({});

const localStorageKey = "recentGlobalSearchResults";
const recentSearchesKey = "recentGlobalSearches";
const RECENT_SEARCH_LIMIT = 5;

const readRecentSearches = () => {
    const storedSearches = getItem({ itemKey: recentSearchesKey, includeTenancyId: true });
    if (!Array.isArray(storedSearches)) {
        return [];
    }

    return storedSearches
        .filter(item => item && typeof item.text === "string" && item.text.trim() && item.lastViewedAt)
        .slice(0, RECENT_SEARCH_LIMIT);
};

const rememberVisitedPage = (state, payload) => {
    const text = (payload?.label || payload?.name || "").trim();
    if (!text) {
        return state.recentSearches || [];
    }

    const recentSearches = _.take(_.uniqBy([
        { text, lastViewedAt: new Date().toISOString() },
        ...((state.recentSearches || []).filter(item => item?.text))
    ], "text"), RECENT_SEARCH_LIMIT);
    setItem({ itemKey: recentSearchesKey, includeTenancyId: true }, recentSearches);

    return recentSearches;
};
export default (state = defaultState(), { type, payload, request, requestId }) => {
    switch (type) {
        case "GLOBAL-SEARCH/INITIALIZE": {
            const { servicesList, featuresByKey, navJson, isRestrictedEnv } = payload;
            const recentResults = getItem({ itemKey: localStorageKey, includeTenancyId: true });
            const recentSearches = readRecentSearches();

            const servicesAndFeatures = servicesList
                .map(service => (_.isArray(service?.uiIntegration?.search) ? service?.uiIntegration?.search : [])
                    .filter(searchItem => {
                        if(!searchItem.featureId) {
                            return true;
                        } else {
                            const feature = featuresByKey[searchItem.featureId];

                            if(!feature.active) {
                                return false;
                            } else {
                                if(isRestrictedEnv) {
                                    return feature.targets.isGov
                                } else {
                                    return true;
                                }
                            }
                        }
                    })
                    .map(searchItem => {
                        const serviceName = service.name;
                        const featureName = searchItem.label || ""; //Set to empty string so it sorts to top

                        let label;
                        let secondaryLabel;

                        if(featureName) {
                            label = featureName;
                            secondaryLabel = serviceName;
                        } else {
                            label = serviceName
                            secondaryLabel = null;
                        }

                        const baseKeywords = [label.toLowerCase()];
                        if(secondaryLabel) {
                            baseKeywords.push(secondaryLabel.toLowerCase())
                        }

                        let href = null;
                        let to = null;
                        if(service.suite !== "CloudManager" && service.service?.to) {
                            href = navJson.suites[service.suite].href + service.service.to;
                        }

                        if(!href) {
                            to = service.service?.to ? service.service.to + searchItem.to : searchItem.to;
                        }

                        return {
                            serviceName,
                            featureName,
                            label,
                            secondaryLabel,
                            to,
                            href,
                            keywords: [...(searchItem.keywords || []), ...baseKeywords],
                            needsConnector: searchItem.featureId ? featuresByKey[searchItem.featureId]?.targets?.needsConnector : false,
                            serviceId: service.newKey,
                            type: "services-and-features"
                        }
                    }))
                .flat()

            const groupedServicesAndFeatures = {};
            servicesAndFeatures.forEach(searchItem => {
                searchItem.keywords.forEach(keyword => {
                    groupedServicesAndFeatures[keyword.toLowerCase()] = groupedServicesAndFeatures[keyword.toLowerCase()] || [];
                    groupedServicesAndFeatures[keyword.toLowerCase()].push(searchItem);
                })
            });

            const servicesAndFeaturesSearcher = new Searcher(Object.keys(groupedServicesAndFeatures), { threshold: 0.75 });

            return { ...state, servicesAndFeaturesSearcher, groupedServicesAndFeatures, recentResults, recentSearches, isInitialized: true }
        }
        case "GLOBAL-SEARCH/SEARCH": {
            const { servicesAndFeaturesSearcher, groupedServicesAndFeatures } = state;
            const { searchTerm } = payload;
            // Console objects, documentation, and knowledge base restart until their own responses arrive.
            const nextSearch = {
                consoleObjects: getEmptyConsoleObjects(),
                documentationResults: null,
                isSearchPerformed: true,
                searchTerm
            };

            if (!servicesAndFeaturesSearcher) {
                return { ...state, ...nextSearch }
            }

            const searchResults = servicesAndFeaturesSearcher.search(searchTerm.toLowerCase());

            const serviceAndFeaturesResults = {
                matchedKeywords: searchResults,
                matchedServicesAndFeatures: _.sortBy([...new Set(searchResults.map(keyword => groupedServicesAndFeatures[keyword]).flat())], ["serviceName", "featureName"])
            }

            return { ...state, ...nextSearch, serviceAndFeaturesResults }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH": {
            const { searchTerm, limit } = payload;

            return {
                ...state,
                consoleObjects: {
                    ...(state.consoleObjects || getEmptyConsoleObjects()),
                    requestedSearchTerm: searchTerm,
                    requestedLimit: limit
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH-PENDING": {
            if(!state.consoleObjects){
                return state;
            }

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    requestId
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH-SUCCESS": {
            if(payload.data === "duplicate call"){
                return { ...state }
            }
            if(isStaleConsoleObjectsResponse(state.consoleObjects, request, requestId)){
                return state;
            }
            // Knowledge base and documentation have their own requests, so this response must not overwrite them
            const { results, count } = mapUnifiedSearchResponse(payload.data);

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    results,
                    count,
                    limit: request.payload.limit,
                    error: false,
                    partialError: false,
                    failedObjectTypes: []
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH-ERROR": {
            if(isStaleConsoleObjectsResponse(state.consoleObjects, request, requestId)){
                return state;
            }
            console.error(payload, "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH");

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    results: [],
                    count: 0,
                    limit: request.payload.limit,
                    error: true,
                    partialError: false,
                    failedObjectTypes: []
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL": {
            const { searchTerm, limit } = payload || {};

            return {
                ...state,
                consoleObjects: {
                    ...(state.consoleObjects || getEmptyConsoleObjects()),
                    requestedSearchTerm: searchTerm,
                    requestedLimit: limit
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL-PENDING": {
            if(!state.consoleObjects){
                return state;
            }

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    requestId
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL-SUCCESS": {
            if(payload.data === "duplicate call"){
                return { ...state }
            }
            if(isStaleConsoleObjectsResponse(state.consoleObjects, request, requestId)){
                return state;
            }
            const results = payload.data?.results || [];
            const count = _.isNumber(payload.data?.count) ? payload.data.count : results.length;

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    results,
                    count,
                    limit: request.payload.limit,
                    error: false,
                    partialError: !!payload.data?.partialError,
                    failedObjectTypes: payload.data?.failedObjectTypes || []
                }
            }
        }
        case "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL-ERROR": {
            if(isStaleConsoleObjectsResponse(state.consoleObjects, request, requestId)){
                return state;
            }
            console.error(payload, "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL");

            return {
                ...state,
                consoleObjects: {
                    ...state.consoleObjects,
                    results: [],
                    count: 0,
                    error: true,
                    partialError: false,
                    failedObjectTypes: []
                }
            };
        }
        case "GLOBAL-SEARCH/DOCUMENTATION/SEARCH":
        case "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH": {
            const sectionKey = referenceRequestKey(type);
            return {
                ...state,
                documentationResults: {
                    ...(state.documentationResults || {}),
                    requests: {
                        ...(state.documentationResults?.requests || {}),
                        [sectionKey]: {
                            searchTerm: payload?.searchTerm,
                            requestId: null
                        }
                    }
                }
            };
        }
        case "GLOBAL-SEARCH/DOCUMENTATION/SEARCH-PENDING":
        case "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH-PENDING": {
            const sectionKey = referenceRequestKey(type);
            const tracked = state.documentationResults?.requests?.[sectionKey];
            if (!tracked) {
                return state;
            }
            return {
                ...state,
                documentationResults: {
                    ...state.documentationResults,
                    requests: {
                        ...state.documentationResults.requests,
                        [sectionKey]: { ...tracked, requestId }
                    }
                }
            };
        }
        case "GLOBAL-SEARCH/DOCUMENTATION/SEARCH-SUCCESS":
        case "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH-SUCCESS": {
            if(payload.data === "duplicate call"){
                return {...state}
            }
            const sectionKey = referenceRequestKey(type);
            if (isStaleReferenceResponse(state.documentationResults, sectionKey, request, requestId)) {
                return state;
            }
            const section = type === "GLOBAL-SEARCH/DOCUMENTATION/SEARCH-SUCCESS" ? "NetApp Documentation" : "Knowledge Base";
            const mapped = mapUnifiedSearchResponse(payload.data);
            const results = mapped.documentationResults?.[section] || [];
            const count = _.isNumber(payload.data?.count) ? payload.data.count : results.length;

            return {
                ...state,
                documentationResults: {
                    ...(state.documentationResults || {}),
                    [section]: results,
                    counts: {
                        ...(state.documentationResults?.counts || {}),
                        [section]: count
                    },
                    errors: {
                        ...(state.documentationResults?.errors || {}),
                        [section]: false
                    }
                }
            }
        }
        case "GLOBAL-SEARCH/DOCUMENTATION/SEARCH-ERROR":
        case "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH-ERROR": {
            const sectionKey = referenceRequestKey(type);
            if (isStaleReferenceResponse(state.documentationResults, sectionKey, request, requestId)) {
                return state;
            }
            const section = type === "GLOBAL-SEARCH/DOCUMENTATION/SEARCH-ERROR" ? "NetApp Documentation" : "Knowledge Base";

            return {
                ...state,
                documentationResults: {
                    ...(state.documentationResults || {}),
                    [section]: [],
                    counts: {
                        ...(state.documentationResults?.counts || {}),
                        [section]: 0
                    },
                    errors: {
                        ...(state.documentationResults?.errors || {}),
                        [section]: true
                    }
                }
            }
        }
        case "GLOBAL-SEARCH/QUERY-TOO-SHORT": {
            return {
                ...state,
                searchTerm: payload,
                isSearchPerformed: false,
                serviceAndFeaturesResults: null,
                documentationResults: null,
                consoleObjects: getEmptyConsoleObjects()
            };
        }
        case "GLOBAL-SEARCH/CLEAR": {
            return { ...state, searchTerm: "", inputValue: "", serviceAndFeaturesResults: null, documentationResults: null, consoleObjects: getEmptyConsoleObjects(), isSearchPerformed: false }
        }
        case "GLOBAL-SEARCH/SET-INPUT": {
            return { ...state, inputValue: payload };
        }
        case "GLOBAL-SEARCH/REMOVE-RECENT-SEARCH": {
            const recentSearches = (state.recentSearches || []).filter(item => item?.text !== payload);
            setItem({ itemKey: recentSearchesKey, includeTenancyId: true }, recentSearches);

            return { ...state, recentSearches };
        }
        case "GLOBAL-SEARCH/OPEN": {
            return { ...state, isOpen: true }
        }
        case "GLOBAL-SEARCH/CLOSE": {
            return { ...state, isOpen: false, searchTerm: "", serviceAndFeaturesResults: null, documentationResults: null, consoleObjects: getEmptyConsoleObjects(), isSearchPerformed: false }
        }
        case "GLOBAL-SEARCH/SELECT-SEARCH/console-object": {
            return { ...state, recentSearches: rememberVisitedPage(state, payload) };
        }
        case "GLOBAL-SEARCH/SELECT-SEARCH/services-and-features": {
            const recentResults = _.take(_.uniqBy([payload, ...(state.recentResults || [])], el => {
                return el.label + "-" + el.secondaryLabel + "-" + el.serviceId
            }), 10);
            setItem({ itemKey: localStorageKey, includeTenancyId: true }, recentResults);

            return { ...state, recentResults, recentSearches: rememberVisitedPage(state, payload) };
        }
        case "GLOBAL-SEARCH/SELECT-SEARCH/knowledge-base":
        case "GLOBAL-SEARCH/SELECT-SEARCH/documentation": {
            const recentResults = _.take(_.uniqBy([payload, ...(state.recentResults || [])], el => {
                return el.label + "-" + el.secondaryLabel
            }), 10);
            setItem({ itemKey: localStorageKey, includeTenancyId: true }, recentResults);

            return { ...state, recentResults, recentSearches: rememberVisitedPage(state, payload) };
        }
        default: {
            return state;
        }
    }
};
